import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import GUI from 'lil-gui'

// ── Constants ─────────────────────────────────────────────────────────────────
const GRID  = 200          // world-space size
const SEGS  = 200          // mesh segment count — higher = smoother waves
const VERT  = SEGS + 1     // vertices per side: 201
const TOTAL = VERT * VERT  // 40 401
const HALF  = GRID / 2
const CELL  = GRID / SEGS  // world units per cell: 0.5

// ── Scene ─────────────────────────────────────────────────────────────────────
const canvas = document.querySelector('canvas.webgl')
const scene  = new THREE.Scene()
scene.background = new THREE.Color(0x05060f)
scene.fog = new THREE.Fog(0x05060f, 160, 310)

// ── Lights (only affect MeshPhong — ships. MeshBasic plane ignores them.) ─────
scene.add(new THREE.AmbientLight(0x334466, 4))
const sun = new THREE.DirectionalLight(0xaaccff, 3)
sun.position.set(25, 50, 35)
scene.add(sun)

// ── Wave plane ────────────────────────────────────────────────────────────────
const geometry = new THREE.PlaneGeometry(GRID, GRID, SEGS, SEGS)
geometry.rotateX(-Math.PI / 2)
const colorArray = new Float32Array(TOTAL * 3)
geometry.setAttribute('color', new THREE.BufferAttribute(colorArray, 3))
const planeMat  = new THREE.MeshBasicMaterial({ vertexColors: true })
const planeMesh = new THREE.Mesh(geometry, planeMat)
planeMesh.frustumCulled = false
scene.add(planeMesh)

// Flat invisible plane for raycasting (stays at y=0)
const rayMesh = new THREE.Mesh(
    new THREE.PlaneGeometry(GRID + 10, GRID + 10),
    new THREE.MeshBasicMaterial({ visible: false, side: THREE.DoubleSide })
)
rayMesh.rotateX(-Math.PI / 2)
scene.add(rayMesh)

// ── Wave state ────────────────────────────────────────────────────────────────
const heights    = new Float32Array(TOTAL)
const velocities = new Float32Array(TOTAL)
const nextH      = new Float32Array(TOTAL)

const params = {
    tool:      'Drop',
    pushWidth: 10,
    tension:   0.20,
    damping:   0.994,
    strength:  4.0,
}

// ── Height sampler — bilinear interpolation from heights[] ────────────────────
function getHeightAt(wx, wz) {
    const fx = (wx + HALF) / CELL
    const fz = (wz + HALF) / CELL
    const x0 = Math.max(0, Math.min(SEGS, Math.floor(fx)))
    const z0 = Math.max(0, Math.min(SEGS, Math.floor(fz)))
    const x1 = Math.min(x0 + 1, SEGS)
    const z1 = Math.min(z0 + 1, SEGS)
    const tx = Math.max(0, Math.min(1, fx - x0))
    const tz = Math.max(0, Math.min(1, fz - z0))
    return heights[z0 * VERT + x0] * (1 - tx) * (1 - tz)
         + heights[z0 * VERT + x1] *      tx  * (1 - tz)
         + heights[z1 * VERT + x0] * (1 - tx) *      tz
         + heights[z1 * VERT + x1] *      tx  *      tz
}

// ── Ships ─────────────────────────────────────────────────────────────────────
// Hull geometry: rectangular stern, pointed bow
// hw=half-width, hh=half-height, L=total length, bowStart=z where taper begins (local -Z = bow)
function createHullGeo(hw, hh, L, bowStart) {
    const halfL = L / 2
    const v = new Float32Array([
        -hw,  hh,  halfL,     // 0  stern TL
         hw,  hh,  halfL,     // 1  stern TR
        -hw, -hh,  halfL,     // 2  stern BL
         hw, -hh,  halfL,     // 3  stern BR
        -hw,  hh, -bowStart,  // 4  mid TL (taper start)
         hw,  hh, -bowStart,  // 5  mid TR
        -hw, -hh, -bowStart,  // 6  mid BL
         hw, -hh, -bowStart,  // 7  mid BR
          0,  hh, -halfL,     // 8  bow top center
          0, -hh, -halfL,     // 9  bow bottom center
    ])
    const idx = [
        0, 3, 1,   0, 2, 3,  // stern face
        0, 1, 5,   0, 5, 4,  // top rect
        4, 5, 8,              // top taper
        0, 4, 2,   4, 6, 2,  // port rect
        4, 9, 6,   4, 8, 9,  // port taper
        1, 3, 5,   5, 3, 7,  // starboard rect
        5, 7, 8,   7, 9, 8,  // starboard taper
        2, 7, 3,   2, 6, 7,  // bottom rect
        6, 9, 7,              // bottom taper
    ]
    const geo = new THREE.BufferGeometry()
    geo.setAttribute('position', new THREE.BufferAttribute(v, 3))
    geo.setIndex(idx)
    geo.computeVertexNormals()
    return geo
}

const SHIP_COLORS  = [0x4477cc, 0xcc3322, 0x33aa55, 0xcc9922, 0x9955aa, 0x22aaaa]
const MAX_TILT     = Math.PI / 9   // ~20° max pitch/roll
const RUDDER_STEPS = [0, Math.PI / 5.5, -Math.PI / 5.5]  // center, port, starboard
const ships = []

let pendingShipClick = null
function cycleRudder(ship) {
    ship.rudderStep   = (ship.rudderStep + 1) % 3
    ship.rudderTarget = RUDDER_STEPS[ship.rudderStep]
}

function createShipMesh(hullColor) {
    const group = new THREE.Group()
    group.rotation.order = 'YXZ'

    const cabinColor = new THREE.Color(hullColor).lerp(new THREE.Color(0xffffff), 0.28)
    const rimColor   = new THREE.Color(0xddddcc)
    const mastColor  = new THREE.Color(0xccbb77)

    const phong = (c) => new THREE.MeshPhongMaterial({ color: c, shininess: 25 })

    // Hull body — pointed bow at local -Z, cabin/stern at +Z
    const hull = new THREE.Mesh(createHullGeo(1, 0.275, 5.2, 0.5), phong(hullColor))
    group.add(hull)

    // White gunwale strip — same shape, slightly wider
    const rim = new THREE.Mesh(createHullGeo(1.04, 0.045, 5.28, 0.52), phong(rimColor))
    rim.position.y = 0.29
    group.add(rim)

    // Cabin / bridge — positioned slightly toward stern (+Z in ship space)
    const cabin = new THREE.Mesh(new THREE.BoxGeometry(1.45, 0.78, 2.2), phong(cabinColor))
    cabin.position.set(0, 0.675, 0.5)
    group.add(cabin)

    // Cabin roof
    const roof = new THREE.Mesh(new THREE.BoxGeometry(1.52, 0.1, 2.26), phong(rimColor))
    roof.position.set(0, 1.12, 0.5)
    group.add(roof)

    // Mast
    const mast = new THREE.Mesh(new THREE.CylinderGeometry(0.06, 0.09, 4.0, 6), phong(mastColor))
    mast.position.set(0, 2.55, 0.5)
    group.add(mast)

    // Small crow's nest at mast top
    const crow = new THREE.Mesh(new THREE.CylinderGeometry(0.25, 0.25, 0.15, 8), phong(mastColor))
    crow.position.set(0, 4.62, 0.5)
    group.add(crow)

    // Rudder — large fin at stern, pivots on vertical axis
    const rudderPivot = new THREE.Group()
    rudderPivot.position.set(0, 0.1, 2.4)
    const rudderBlade = new THREE.Mesh(
        new THREE.BoxGeometry(0.18, 2.0, 2.2),
        phong(0xff5500)
    )
    rudderBlade.position.set(0, -0.3, 1.1)  // extends behind pivot, centered slightly below
    rudderPivot.add(rudderBlade)
    group.add(rudderPivot)

    return { group, rudderPivot }
}

function spawnShips() {
    const margin = 10
    const range  = HALF - margin
    for (let i = 0; i < 6; i++) {
        const wx  = (Math.random() * 2 - 1) * range
        const wz  = (Math.random() * 2 - 1) * range
        const yaw = Math.random() * Math.PI * 2
        const { group, rudderPivot } = createShipMesh(SHIP_COLORS[i])
        group.position.set(wx, 0, wz)
        scene.add(group)
        const ship = {
            group, rudderPivot,
            wx, wz,
            yaw,
            pitch: 0, roll: 0,
            vx: 0,   vz: 0,
            rudderStep: 0, rudderTarget: 0, rudderAngle: 0,
        }
        group.traverse(obj => { if (obj.isMesh) obj.userData.shipIdx = i })
        ships.push(ship)
    }
}
spawnShips()

// ── Stone walls ───────────────────────────────────────────────────────────────
function buildWalls() {
    const STEP  = 3.8       // spacing between stone centres along the wall
    const DEPTH = 4.2       // wall thickness (perpendicular to the water edge)
    const BASE  = -1.4      // base Y — slightly submerged for a grounded look

    const stoneColors = [0x38383a, 0x464642, 0x4c3e35, 0x525254, 0x3f3f47, 0x403830, 0x404040, 0x4a4030]
    const phong = c => new THREE.MeshPhongMaterial({ color: c, shininess: 2 })

    const group = new THREE.Group()

    function addStone(cx, cz, alongX) {
        const sw = STEP  * (0.55 + Math.random() * 0.80)  // size along the wall
        const sh = 2.6   + Math.random() * 3.8             // height
        const sd = DEPTH * (0.50 + Math.random() * 0.75)  // depth into the wall
        const color = stoneColors[Math.floor(Math.random() * stoneColors.length)]
        const geo = new THREE.BoxGeometry(
            alongX ? sw : sd,
            sh,
            alongX ? sd : sw
        )
        const mesh = new THREE.Mesh(geo, phong(color))
        mesh.position.set(
            cx + (Math.random() - 0.5) * 0.6,
            BASE + sh / 2,
            cz + (Math.random() - 0.5) * 0.6
        )
        mesh.rotation.y = (Math.random() - 0.5) * 0.12
        group.add(mesh)
    }

    // North (z = -HALF) and South (z = +HALF) — stones run along X
    for (const wallZ of [-HALF, HALF]) {
        for (let x = -HALF; x <= HALF + STEP * 0.5; x += STEP) addStone(x, wallZ, true)
    }

    // West (x = -HALF) and East (x = +HALF) — stones run along Z (skip corner overlap)
    for (const wallX of [-HALF, HALF]) {
        for (let z = -HALF + STEP; z < HALF; z += STEP) addStone(wallX, z, false)
    }

    scene.add(group)
}
buildWalls()

// ── Drop cursor (bobbing icosahedron) ─────────────────────────────────────────
const dropCursor = new THREE.Mesh(
    new THREE.IcosahedronGeometry(0.65, 1),
    new THREE.MeshBasicMaterial({ color: 0xffe066, wireframe: true })
)
dropCursor.visible = false
scene.add(dropCursor)

// ── Push cursor (bar + directional arrow) ─────────────────────────────────────
const pushGroup = new THREE.Group()
pushGroup.visible = false
scene.add(pushGroup)

let pushBarMesh = null
function rebuildPushBar() {
    if (pushBarMesh) { pushGroup.remove(pushBarMesh); pushBarMesh.geometry.dispose() }
    pushBarMesh = new THREE.Mesh(
        new THREE.BoxGeometry(params.pushWidth, 0.35, 0.5),
        new THREE.MeshBasicMaterial({ color: 0xff6622 })
    )
    pushGroup.add(pushBarMesh)
}
rebuildPushBar()

const arrowHelper = new THREE.ArrowHelper(
    new THREE.Vector3(0, 0, 1),   // push direction = +Z when angle 0
    new THREE.Vector3(0, 0.2, 0),
    3.5, 0xffaa33, 1.1, 0.55
)
pushGroup.add(arrowHelper)

let pushAngle = 0

// ── Falling animations ────────────────────────────────────────────────────────
const falls = []

function spawnFall(mesh, startY, duration, onLand) {
    mesh.position.y = startY
    scene.add(mesh)
    falls.push({ mesh, startY, progress: 0, duration, onLand })
}

function spawnDropFall(tileX, tileZ, wx, wz, startY, strength) {
    const m = new THREE.Mesh(
        new THREE.IcosahedronGeometry(0.65, 1),
        new THREE.MeshBasicMaterial({ color: 0xffe066, wireframe: true })
    )
    m.position.set(wx, 0, wz)
    const duration = 0.28 + (startY - 7) * 0.018  // longer fall from higher up
    spawnFall(m, startY, duration, () => applyDrop(tileX, tileZ, strength))
}

function spawnPushFall(tileX, tileZ, wx, wz, angle, width, startY, strength) {
    const m = new THREE.Mesh(
        new THREE.BoxGeometry(width, 0.35, 0.5),
        new THREE.MeshBasicMaterial({ color: 0xff6622 })
    )
    m.rotation.y = angle
    m.position.set(wx, 0, wz)
    const duration = 0.28 + (startY - 7) * 0.018
    spawnFall(m, startY, duration, () => applyPush(tileX, tileZ, angle, width, strength))
}

// ── Wave application ──────────────────────────────────────────────────────────
function applyDrop(tileX, tileZ, strength) {
    const str = strength ?? params.strength
    for (let dz = 0; dz <= 1; dz++)
        for (let dx = 0; dx <= 1; dx++) {
            const i = (tileZ + dz) * VERT + (tileX + dx)
            heights[i] = -str; velocities[i] = 0
        }
}

function applyPush(startVX, startVZ, angle, width, strength) {
    const barDX = Math.cos(angle), barDZ = -Math.sin(angle)
    const str   = strength ?? params.strength
    const halfW = width / (2 * CELL)
    for (let t = -halfW; t <= halfW; t += 0.5) {
        const vx = Math.round(startVX + t * barDX)
        const vz = Math.round(startVZ + t * barDZ)
        if (vx >= 0 && vx <= SEGS && vz >= 0 && vz <= SEGS) {
            heights[vz * VERT + vx] = -str; velocities[vz * VERT + vx] = 0
        }
    }
}

// ── Push preview ──────────────────────────────────────────────────────────────
const previewVerts = new Set()
function updatePushPreview(tileX, tileZ) {
    previewVerts.clear()
    if (params.tool !== 'Push' || tileX < 0) return
    const barDX = Math.cos(pushAngle), barDZ = -Math.sin(pushAngle)
    const halfW = params.pushWidth / (2 * CELL)
    for (let t = -halfW; t <= halfW; t += 0.5) {
        const vx = Math.round(tileX + t * barDX)
        const vz = Math.round(tileZ + t * barDZ)
        if (vx >= 0 && vx <= SEGS && vz >= 0 && vz <= SEGS)
            previewVerts.add(vz * VERT + vx)
    }
}

// ── Mouse & keyboard ──────────────────────────────────────────────────────────
const mouse     = new THREE.Vector2(-9999, -9999)
const raycaster = new THREE.Raycaster()
let hoverX = -1, hoverZ = -1

window.addEventListener('mousemove', (e) => {
    mouse.x =  (e.clientX / window.innerWidth)  * 2 - 1
    mouse.y = -(e.clientY / window.innerHeight) * 2 + 1
})

// ── Charge mechanic: hold to raise the tool, release to fire ──────────────────
const MAX_CHARGE   = 2.0   // seconds to reach full charge
const MAX_LIFT     = 22    // extra world-units height at full charge
const MAX_STR_MULT = 3.0   // strength multiplier at full charge

let isCharging  = false
let chargeStart = 0        // performance.now() when mousedown began
let chargedTile = { x: -1, z: -1, wx: 0, wz: 0 }

window.addEventListener('mousedown', (e) => {
    if (e.button !== 0) return

    // Check for ship click before starting wave charge
    raycaster.setFromCamera(mouse, camera)
    const shipHits = raycaster.intersectObjects(ships.map(s => s.group), true)
    if (shipHits.length > 0) {
        const idx = shipHits[0].object.userData.shipIdx
        if (idx !== undefined) { pendingShipClick = ships[idx]; return }
    }

    if (hoverX < 0 || hoverZ < 0) return
    isCharging  = true
    chargeStart = performance.now()
    chargedTile = {
        x: hoverX, z: hoverZ,
        wx: hoverX * CELL - HALF + CELL * 0.5,
        wz: hoverZ * CELL - HALF + CELL * 0.5,
    }
})

window.addEventListener('mouseup', (e) => {
    if (e.button !== 0) return

    if (pendingShipClick) {
        cycleRudder(pendingShipClick)
        pendingShipClick = null
        return
    }

    if (!isCharging) return
    isCharging = false
    if (chargedTile.x < 0) return
    const charge = Math.min(1, (performance.now() - chargeStart) / (MAX_CHARGE * 1000))
    const startY = 7 + charge * MAX_LIFT
    const str    = params.strength * (1 + charge * (MAX_STR_MULT - 1))
    if (params.tool === 'Drop')
        spawnDropFall(chargedTile.x, chargedTile.z, chargedTile.wx, chargedTile.wz, startY, str)
    else
        spawnPushFall(chargedTile.x, chargedTile.z, chargedTile.wx, chargedTile.wz, pushAngle, params.pushWidth, startY, str)
    chargedTile = { x: -1, z: -1, wx: 0, wz: 0 }
})

let toolCtrl
window.addEventListener('keydown', (e) => {
    const step = Math.PI / 16
    if (e.key === '1') { params.tool = 'Drop'; toolCtrl?.updateDisplay(); updateHint() }
    if (e.key === '2') { params.tool = 'Push'; toolCtrl?.updateDisplay(); updateHint() }
    if (e.key === 'q' || e.key === 'Q' || e.key === 'ArrowLeft')  pushAngle -= step
    if (e.key === 'e' || e.key === 'E' || e.key === 'ArrowRight') pushAngle += step
})

// ── Resize ────────────────────────────────────────────────────────────────────
const sizes = { width: window.innerWidth, height: window.innerHeight }
window.addEventListener('resize', () => {
    sizes.width  = window.innerWidth
    sizes.height = window.innerHeight
    camera.aspect = sizes.width / sizes.height
    camera.updateProjectionMatrix()
    renderer.setSize(sizes.width, sizes.height)
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
})

// ── Camera ────────────────────────────────────────────────────────────────────
const camera = new THREE.PerspectiveCamera(60, sizes.width / sizes.height, 0.1, 500)
camera.position.set(0, 90, 140)
camera.lookAt(0, 0, 0)
scene.add(camera)

const controls = new OrbitControls(camera, canvas)
controls.enableDamping = true

// ── Renderer ──────────────────────────────────────────────────────────────────
const renderer = new THREE.WebGLRenderer({ canvas, antialias: true })
renderer.setSize(sizes.width, sizes.height)
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))

// ── GUI ───────────────────────────────────────────────────────────────────────
const gui = new GUI()
toolCtrl = gui.add(params, 'tool', ['Drop', 'Push']).name('Tool  [1 / 2]').onChange(updateHint)
gui.add(params, 'pushWidth', 3, 30, 1).name('Push width').onChange(rebuildPushBar)
gui.add(params, 'tension',  0.05, 0.24, 0.01).name('Wave speed')
gui.add(params, 'damping',  0.96, 0.999, 0.001).name('Persistence')
gui.add(params, 'strength', 1, 10, 0.5).name('Strength')

// ── Hint ──────────────────────────────────────────────────────────────────────
const hintEl = document.getElementById('hint')
function updateHint() {
    const base = params.tool === 'Drop'
        ? 'Drop tool · hold to charge · release to drop'
        : 'Push tool · Q / E to rotate · hold to charge · release to push'
    hintEl.textContent = base + '  ·  click a ship to steer it'
}
updateHint()

// ── Clock ─────────────────────────────────────────────────────────────────────
const clock = new THREE.Clock()

// ── Tick ──────────────────────────────────────────────────────────────────────
function tick() {
    const delta   = clock.getDelta()
    const elapsed = clock.getElapsedTime()

    const positions = planeMesh.geometry.attributes.position.array
    const colors    = planeMesh.geometry.attributes.color.array
    const isPush    = params.tool === 'Push'

    // ── Hover ─────────────────────────────────────────────────────────────────
    raycaster.setFromCamera(mouse, camera)
    const hits = raycaster.intersectObject(rayMesh)
    if (hits.length) {
        const p  = hits[0].point
        const tx = Math.floor((p.x + HALF) / CELL)
        const tz = Math.floor((p.z + HALF) / CELL)
        hoverX = (tx >= 0 && tx < SEGS) ? tx : -1
        hoverZ = (tz >= 0 && tz < SEGS) ? tz : -1
    } else {
        hoverX = -1; hoverZ = -1
    }

    updatePushPreview(hoverX, hoverZ)

    // ── Cursors ───────────────────────────────────────────────────────────────
    const onGrid = hoverX >= 0 && hoverZ >= 0
    const wx = hoverX * CELL - HALF + CELL * 0.5
    const wz = hoverZ * CELL - HALF + CELL * 0.5
    const bob = Math.sin(elapsed * 3) * 0.3

    const charge = isCharging
        ? Math.min(1, (performance.now() - chargeStart) / (MAX_CHARGE * 1000))
        : 0
    const lift = charge * MAX_LIFT

    dropCursor.visible = !isPush && onGrid
    pushGroup.visible  =  isPush && onGrid
    if (onGrid) {
        if (!isPush) {
            dropCursor.position.set(wx, 3.2 + bob + lift, wz)
            dropCursor.rotation.x = elapsed * 1.1
            dropCursor.rotation.y = elapsed * 0.9
            const cs = 1 + charge * 0.6
            dropCursor.scale.setScalar(cs)
        } else {
            pushGroup.position.set(wx, 2.8 + bob + lift, wz)
            pushGroup.rotation.y = pushAngle
            pushGroup.scale.setScalar(1 + charge * 0.4)
        }
    }

    // ── Falls ─────────────────────────────────────────────────────────────────
    for (let i = falls.length - 1; i >= 0; i--) {
        const f = falls[i]
        f.progress += delta / f.duration
        if (f.progress >= 1) {
            f.onLand()
            scene.remove(f.mesh)
            f.mesh.geometry.dispose()
            f.mesh.material.dispose()
            falls.splice(i, 1)
        } else {
            const t = f.progress * f.progress  // gravity ease-in
            f.mesh.position.y = f.startY * (1 - t)
        }
    }

    // ── Wave simulation ───────────────────────────────────────────────────────
    const { tension, damping } = params
    for (let z = 0; z < VERT; z++) {
        for (let x = 0; x < VERT; x++) {
            const i = z * VERT + x
            let nSum = 0, nCnt = 0
            if (x > 0)        { nSum += heights[i - 1];    nCnt++ }
            if (x < VERT - 1) { nSum += heights[i + 1];    nCnt++ }
            if (z > 0)        { nSum += heights[i - VERT]; nCnt++ }
            if (z < VERT - 1) { nSum += heights[i + VERT]; nCnt++ }
            const avg = nCnt > 0 ? nSum / nCnt : 0
            velocities[i] = (velocities[i] + (avg - heights[i]) * tension) * damping
            nextH[i]      = heights[i] + velocities[i]
        }
    }
    heights.set(nextH)

    // ── Wave plane colors + positions ─────────────────────────────────────────
    const str = params.strength
    for (let z = 0; z < VERT; z++) {
        for (let x = 0; x < VERT; x++) {
            const i = z * VERT + x
            const h = heights[i]
            positions[i * 3 + 1] = h

            const t = Math.max(-1, Math.min(1, h / str))
            let r, g, b
            if (t >= 0) {
                r = 0.05  * (1 - t)
                g = 0.15  + t * 0.78
                b = 0.31  + t * 0.69
            } else {
                const u = 1 + t
                r = 0.005 + u * 0.045
                g = 0.02  + u * 0.13
                b = 0.05  + u * 0.26
            }

            // Drop hover: blue tile
            if (!isPush && onGrid &&
                x >= hoverX && x <= hoverX + 1 &&
                z >= hoverZ && z <= hoverZ + 1) {
                r += (0.38 - r) * 0.72
                g += (0.72 - g) * 0.72
                b += (1.00 - b) * 0.72
            }

            // Push preview: orange line
            if (isPush && previewVerts.has(i)) {
                r += (1.00 - r) * 0.65
                g += (0.45 - g) * 0.65
                b += (0.05 - b) * 0.65
            }

            colors[i * 3]     = r
            colors[i * 3 + 1] = g
            colors[i * 3 + 2] = b
        }
    }

    planeMesh.geometry.attributes.position.needsUpdate = true
    planeMesh.geometry.attributes.color.needsUpdate    = true

    // ── Ships ─────────────────────────────────────────────────────────────────
    for (const ship of ships) {
        const sinY = Math.sin(ship.yaw)
        const cosY = Math.cos(ship.yaw)

        // Sample heights along ship's local forward and right axes (±2 units)
        const hFront = getHeightAt(ship.wx + sinY * 2, ship.wz + cosY * 2)
        const hBack  = getHeightAt(ship.wx - sinY * 2, ship.wz - cosY * 2)
        const hRight = getHeightAt(ship.wx + cosY * 2, ship.wz - sinY * 2)
        const hLeft  = getHeightAt(ship.wx - cosY * 2, ship.wz + sinY * 2)

        // Smooth tilt angles (lerp toward wave slope, clamped to MAX_TILT)
        const targetPitch = Math.max(-MAX_TILT, Math.min(MAX_TILT, Math.atan2(hFront - hBack, 4)))
        const targetRoll  = Math.max(-MAX_TILT, Math.min(MAX_TILT, Math.atan2(hRight - hLeft, 4)))
        ship.pitch += (targetPitch - ship.pitch) * 0.12
        ship.roll  += (targetRoll  - ship.roll)  * 0.12

        // Vertical position: ride the wave surface, lifted slightly above waterline
        ship.group.position.y = getHeightAt(ship.wx, ship.wz) + 0.45

        // Apply rotation: yaw preserved, pitch + roll from wave
        ship.group.rotation.y = ship.yaw
        ship.group.rotation.x = ship.pitch
        ship.group.rotation.z = ship.roll

        // Wave gradient at ship centre (downhill = force direction)
        const gx = getHeightAt(ship.wx - 0.5, ship.wz) - getHeightAt(ship.wx + 0.5, ship.wz)
        const gz = getHeightAt(ship.wx, ship.wz - 0.5) - getHeightAt(ship.wx, ship.wz + 0.5)

        // Decompose gradient into bow/stern axis (sinY,cosY) and beam axis (cosY,-sinY).
        // Waves hitting the bow/stern drag the ship at near wave-speed (high scale).
        // Waves hitting the side barely move it — hull resists beam pressure (low scale).
        const fwdMag  = gx * sinY + gz * cosY   // projection along ship heading
        const sideMag = gx * cosY - gz * sinY   // projection across the beam

        // ── Rudder ────────────────────────────────────────────────────────────
        ship.rudderAngle += (ship.rudderTarget - ship.rudderAngle) * 0.07
        ship.rudderPivot.rotation.y = ship.rudderAngle
        const rudderSin = Math.sin(ship.rudderAngle)
        const fwdSpeed  = ship.vx * sinY + ship.vz * cosY
        ship.yaw += fwdSpeed * rudderSin * 0.030  // forward motion turns the boat
        ship.yaw += sideMag  * rudderSin * 0.018  // side wave force also turns via rudder
        const drag = 1 - Math.abs(rudderSin) * 0.10

        const ax = fwdMag * sinY * 0.04 + sideMag * cosY * 0.02
        const az = fwdMag * cosY * 0.04 - sideMag * sinY * 0.02

        ship.vx = Math.max(-0.6, Math.min(0.6, (ship.vx + ax) * 0.985 * drag))
        ship.vz = Math.max(-0.6, Math.min(0.6, (ship.vz + az) * 0.985 * drag))
        ship.wx = Math.max(-HALF + 8, Math.min(HALF - 8, ship.wx + ship.vx))
        ship.wz = Math.max(-HALF + 8, Math.min(HALF - 8, ship.wz + ship.vz))

        ship.group.position.x = ship.wx
        ship.group.position.z = ship.wz
    }

    controls.update()
    renderer.render(scene, camera)
    requestAnimationFrame(tick)
}

tick()
